import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import CancelIcon from '@mui/icons-material/CloseOutlined';
import toast from 'react-hot-toast';
import {
  getEmailSettings,
  updateEmailSender,
  updateEmailTrigger,
  sendTestEmail,
} from '../api/emailSettingsApi';

const TRANSPORT_LABEL = {
  smtp: 'Generic SMTP',
  gmail: 'Gmail (service)',
  none: 'Not configured',
};

function senderHeadline(sender) {
  if (!sender || !sender.from_address) return 'No sender configured';
  return sender.from_name
    ? `${sender.from_name} <${sender.from_address}>`
    : sender.from_address;
}

export default function EmailSettingsPanel() {
  const qc = useQueryClient();
  const [editingSender, setEditingSender] = useState(false);
  const [draft, setDraft] = useState({ from_address: '', from_name: '', reply_to: '' });
  const [testTo, setTestTo] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['email-settings'],
    queryFn: getEmailSettings,
  });

  const sender = data?.sender;
  const triggers = useMemo(() => data?.triggers || [], [data]);

  useEffect(() => {
    if (sender && !editingSender) {
      setDraft({
        from_address: sender.from_address || '',
        from_name: sender.from_name || '',
        reply_to: sender.reply_to || '',
      });
    }
  }, [sender, editingSender]);

  const senderMutation = useMutation({
    mutationFn: updateEmailSender,
    onSuccess: () => {
      toast.success('Sender identity saved');
      setEditingSender(false);
      qc.invalidateQueries({ queryKey: ['email-settings'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save sender'),
  });

  const triggerMutation = useMutation({
    mutationFn: ({ key, enabled }) => updateEmailTrigger(key, enabled),
    onMutate: async ({ key, enabled }) => {
      await qc.cancelQueries({ queryKey: ['email-settings'] });
      const previous = qc.getQueryData(['email-settings']);
      if (previous) {
        const next = {
          ...previous,
          triggers: previous.triggers.map((g) => ({
            ...g,
            items: g.items.map((it) => (it.key === key ? { ...it, enabled } : it)),
          })),
        };
        qc.setQueryData(['email-settings'], next);
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['email-settings'], ctx.previous);
      toast.error(err.response?.data?.message || 'Failed to update trigger');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-settings'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (to) => sendTestEmail(to),
    onSuccess: () => toast.success('Test email sent'),
    onError: (e) => toast.error(e.response?.data?.message || 'Test email failed'),
  });

  const totalEnabled = useMemo(
    () => triggers.reduce((acc, g) => acc + g.items.filter((i) => i.enabled).length, 0),
    [triggers]
  );

  const totalCount = useMemo(
    () => triggers.reduce((acc, g) => acc + g.items.length, 0),
    [triggers]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error.response?.data?.message || 'Failed to load email settings.'}
      </Alert>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      {/* ── Sender identity card ────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Sender identity</Typography>
            <Chip
              size="small"
              label={sender?.configured ? `Mailer: ${TRANSPORT_LABEL[sender.transport_mode] || sender.transport_mode}` : 'Mailer not configured'}
              color={sender?.configured ? 'success' : 'warning'}
              variant="outlined"
              sx={{ ml: 1, fontWeight: 600 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The From name and address used on every notification email. SMTP credentials
            stay in the backend .env file — only the visible identity is editable here.
          </Typography>

          {!editingSender ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="text.secondary">From</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                  {senderHeadline(sender)}
                </Typography>
                {sender?.reply_to ? (
                  <Typography variant="caption" color="text.secondary">
                    Reply-to: {sender.reply_to}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  onClick={() => setEditingSender(true)}
                >
                  Edit
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Display name"
                  value={draft.from_name}
                  onChange={(e) => setDraft((d) => ({ ...d, from_name: e.target.value }))}
                  placeholder="Team Space"
                  size="small"
                  fullWidth
                />
                <TextField
                  label="From address"
                  value={draft.from_address}
                  onChange={(e) => setDraft((d) => ({ ...d, from_address: e.target.value }))}
                  placeholder="noreply.teamspace@yourcompany.com"
                  size="small"
                  fullWidth
                  required
                />
              </Stack>
              <TextField
                label="Reply-to (optional)"
                value={draft.reply_to}
                onChange={(e) => setDraft((d) => ({ ...d, reply_to: e.target.value }))}
                placeholder="hr-support@yourcompany.com"
                size="small"
                helperText="Set this if replies should go to a different mailbox than the From address."
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditingSender(false);
                    setDraft({
                      from_address: sender?.from_address || '',
                      from_name: sender?.from_name || '',
                      reply_to: sender?.reply_to || '',
                    });
                  }}
                  disabled={senderMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  onClick={() => senderMutation.mutate(draft)}
                  disabled={!draft.from_address || senderMutation.isPending}
                >
                  {senderMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label="Send test email to"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={sender?.from_address || 'you@company.com'}
              size="small"
              sx={{ minWidth: 320 }}
            />
            <Tooltip title={!sender?.configured ? 'Mailer is not configured (set EMAIL_USER / EMAIL_PASS in backend .env)' : ''}>
              <span>
                <Button
                  startIcon={<SendIcon />}
                  variant="outlined"
                  onClick={() => testMutation.mutate(testTo || undefined)}
                  disabled={testMutation.isPending || !sender?.configured}
                >
                  {testMutation.isPending ? 'Sending…' : 'Send test email'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Trigger toggles ────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>Notification triggers</Typography>
            <Chip
              size="small"
              label={`${totalEnabled} of ${totalCount} on`}
              color={totalEnabled > 0 ? 'success' : 'default'}
              variant="outlined"
              sx={{ ml: 1, fontWeight: 600 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which events send email. Each toggle takes effect immediately.
          </Typography>

          {triggers.length === 0 ? (
            <Alert severity="info">No triggers registered yet.</Alert>
          ) : (
            <Stack spacing={2.5} divider={<Divider flexItem />}>
              {triggers.map((group) => (
                <Box key={group.domain}>
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', letterSpacing: 1, fontWeight: 700 }}
                  >
                    {group.domain}
                  </Typography>
                  <Stack divider={<Divider flexItem />}>
                    {group.items.map((item) => (
                      <Stack
                        key={item.key}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 1.5 }}
                      >
                        <Box sx={{ pr: 2 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                            {item.key}
                          </Typography>
                        </Box>
                        <Switch
                          checked={!!item.enabled}
                          onChange={(e) =>
                            triggerMutation.mutate({ key: item.key, enabled: e.target.checked })
                          }
                          color="primary"
                          inputProps={{ 'aria-label': `Toggle ${item.label}` }}
                          disabled={!sender?.configured}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {!sender?.configured && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Triggers are disabled until SMTP is configured. Add EMAIL_USER and EMAIL_PASS
              (and optional EMAIL_HOST / EMAIL_PORT / EMAIL_SECURE) to the backend .env file
              and restart the server.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
